import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, IconButton,
    List, ListItem, ListItemText, ListItemSecondaryAction,
    CircularProgress, Divider
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useGetSettingByKeyQuery, useUpsertSettingMutation } from "../../../../../reducers/settingsApi";
import UpdateButton from "../../../multivendor/components/UpdateButton";
import { toast } from "react-toastify";

const ManageIgnoredCompetitors = () => {
    const settingKey = "app.setting.ignoreCompetitor";
    const { data: settingRes, isLoading: isFetching } = useGetSettingByKeyQuery(settingKey);
    const [upsertSetting, { isLoading: isUpdating }] = useUpsertSettingMutation();

    const [competitors, setCompetitors] = useState([]);
    const [newCompetitor, setNewCompetitor] = useState("");

    useEffect(() => {
        if (settingRes?.data?.value) {
            setCompetitors(Array.isArray(settingRes.data.value) ? settingRes.data.value : [settingRes.data.value]);
        }
    }, [settingRes]);

    const updateSettings = async (updatedList) => {
        try {
            const payload = {
                key: settingKey,
                value: updatedList,
                description: "List of Competitors to ignore in pricing calculations",
                category: "app",
                isPublic: true
            };

            await upsertSetting(payload).unwrap();
            setCompetitors(updatedList);
            setNewCompetitor("");
            toast.success("Ignored competitors updated successfully");
        } catch (error) {
            console.error("Failed to save competitors:", error);
            toast.error(error?.data?.message || "Failed to update ignored competitors");
        }
    };

    const handleRemoveCompetitor = async (competitorToRemove) => {
        const updatedList = competitors.filter(c => c !== competitorToRemove);
        await updateSettings(updatedList);
    };

    const handleSave = async () => {
        let updatedList = [...competitors];
        const trimmed = newCompetitor.trim();

        if (trimmed) {
            if (updatedList.includes(trimmed)) {
                toast.warning("Competitor already exists in the list");
                return;
            } else {
                updatedList = [...updatedList, trimmed];
            }
        }

        await updateSettings(updatedList);
    };

    if (isFetching) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "#000" }}>
                Manage Ignored Competitors
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
                Add competitor names or domains below. These will be excluded from the pricing sidebar calculations and "Pivot Item" selection.
            </Typography>

            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Add Competitor Name/Domain"
                    placeholder="e.g. Amazon, Wayfair, etc."
                    value={newCompetitor}
                    onChange={(e) => setNewCompetitor(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSave()}
                />
            </Box>

            <Divider />

            <List sx={{ maxHeight: "400px", overflowY: "auto", mt: 1 }}>
                {competitors.length === 0 ? (
                    <Typography sx={{ textAlign: "center", py: 4, color: "#999", fontStyle: "italic" }}>
                        No competitors ignored yet.
                    </Typography>
                ) : (
                    competitors.map((competitor, index) => (
                        <ListItem key={index} divider sx={{ px: 1 }}>
                            <ListItemText 
                                primary={competitor} 
                                primaryTypographyProps={{ sx: { fontWeight: 500 } }}
                            />
                            <ListItemSecondaryAction>
                                <IconButton edge="end" onClick={() => handleRemoveCompetitor(competitor)} color="error" size="small">
                                    <DeleteIcon />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))
                )}
            </List>

            <Box sx={{ mt: 3 }}>
                <UpdateButton
                    position="relative"
                    brand="Competitors"
                    loading={isUpdating}
                    onClick={handleSave}
                />
            </Box>
        </Box>
    );
};

export default ManageIgnoredCompetitors;
